// Sugalarm — inline SVG trend-arrow glyph for the KioskHeader's
// mini readout (cycle 0.65.7).
//
// The cycle-0.64.1 fixed-box centering pinned the **bounding box** of
// the Unicode arrow glyph (↑ ↗ → ↘ ↓ ↓↓) against the number, but
// different rendering engines (Pi Chromium, iOS WKWebView, Android
// WebView, desktop Chromium) draw the same glyph at slightly
// different vertical positions inside that box because Manrope's
// per-glyph baseline metrics aren't identical across engines. The
// only robust fix is to stop using a font glyph: this component
// renders each direction as an SVG path in a fixed `0 0 22 22`
// viewBox so the geometry is byte-identical on every platform.
//
// Map by glyph string — same vocabulary `trendInfo()` already returns
// — so KioskHeader (or any other consumer) keeps passing the same
// `trendGlyph` string and switches its render path with one swap.
// The hero `<TrendArrow>` on the NowScreen stays font-based per the
// cycle-0.64.1 carve-out (it's surrounded by a much larger
// `var(--hero-font)` numeric and visually matches it best as a font
// glyph).

interface Props {
  /** The Unicode trend glyph string `trendInfo()` returns. Used as
   *  the discriminator; the SVG path is drawn from a fixed lookup. */
  glyph: string;
  /** Pixel size (width === height). Default 22 — the KioskHeader
   *  mini-cluster's existing footprint. */
  size?: number;
  /** Stroke color. Default `currentColor` so the parent's `color`
   *  cascade controls tone (the KioskHeader passes
   *  `color: stale ? var(--ink-3) : zoneColor(zone)` on the wrapper). */
  color?: string;
  /** Stroke width in viewBox units (the viewBox is 22×22). Default
   *  2.2 — reads comparable in weight to a 500-weight Manrope arrow
   *  at this scale. */
  strokeWidth?: number;
}

// Each path is drawn inside a 0 0 22 22 viewBox so the glyph sits in
// a square sub-pill that aligns with the number's optical center
// when wrapped by the KioskHeader's `alignItems: center` flex row.
// Stroke-based (not filled) so the line weight scales linearly with
// the wrapper's stroke prop. Round join + cap so the head fragments
// read smoothly.
function pathFor(glyph: string): string {
  switch (glyph) {
    case "↑":
      // Vertical up: shaft from (11,18) → (11,4), head at top.
      return "M11 18 L11 4 M5 10 L11 4 L17 10";
    case "↗":
      // Diagonal up-right: shaft from (5,17) → (17,5), head at TR.
      return "M5 17 L17 5 M9 5 L17 5 L17 13";
    case "↘":
      // Diagonal down-right: shaft from (5,5) → (17,17), head at BR.
      return "M5 5 L17 17 M17 9 L17 17 L9 17";
    case "↓":
      // Vertical down: shaft from (11,4) → (11,18), head at bottom.
      return "M11 4 L11 18 M5 12 L11 18 L17 12";
    case "↓↓":
      // Double down: two narrower vertical-down arrows side-by-side.
      return [
        "M7 4 L7 18 M3 12 L7 18 L11 12",
        "M15 4 L15 18 M11 12 L15 18 L19 12",
      ].join(" ");
    case "→":
    default:
      // Horizontal right (also the default for empty / unknown
      // glyphs so a render before `trendInfo()` lands doesn't crash).
      return "M4 11 L18 11 M12 5 L18 11 L12 17";
  }
}

export function TrendGlyphSvg({
  glyph,
  size = 22,
  color = "currentColor",
  strokeWidth = 2.2,
}: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      style={{ display: "block", flexShrink: 0 }}
      aria-hidden="true"
    >
      <path
        d={pathFor(glyph)}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
